/**
 * Choose Letter to Preview — shown when multiple bureau letters exist in one package group.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { formatLetterDate, getLetterAttachmentCount } from '../../pages/disputes/disputeCenterUtils';

const STATUS_CLASS = {
  ready: 'text-emerald-400',
  generated: 'text-slate-300',
  draft: 'text-slate-400',
  printing: 'text-amber-400',
  processed: 'text-amber-400',
  sent: 'text-sky-400',
  delivered: 'text-emerald-400',
  completed: 'text-emerald-400',
  cancelled: 'text-rose-400',
  failed: 'text-rose-400',
  unknown: 'text-slate-400',
};

function statusClass(status) {
  return STATUS_CLASS[status] || STATUS_CLASS.unknown;
}

const ChooseLetterPreviewModal = ({
  isOpen,
  onClose,
  letters = [],
  onSelect,
  statusLabel,
  defaultLetterId = null,
  title = 'Choose Letter to Preview',
  subtitle = 'Select a generated letter to open the full preview with its supporting documents.',
}) => {
  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="choose-letter-preview-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] flex items-center justify-center"
          data-testid="choose-letter-preview-modal"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[rgba(6,28,20,0.85)] backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.2 }}
            className="relative z-10 w-full max-w-5xl max-h-[85vh] m-4 flex flex-col bg-stone-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-700"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">{title}</h2>
                  <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                data-testid="choose-letter-preview-close"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex-1 overflow-auto p-4 sm:p-6">
              <div className="overflow-x-auto rounded-xl border border-slate-700">
                <table className="w-full text-sm text-left">
                  <thead className="bg-stone-800/80 text-slate-400 uppercase text-xs tracking-wide">
                    <tr>
                      <th className="px-4 py-3 font-medium">Bureau</th>
                      <th className="px-4 py-3 font-medium">Letter Type</th>
                      <th className="px-4 py-3 font-medium">Recipient</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Created</th>
                      <th className="px-4 py-3 font-medium text-center">Attachments</th>
                      <th className="px-4 py-3 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {letters.map((letter) => {
                      const attachmentCount = getLetterAttachmentCount(letter);
                      const isDefault = letter.id === defaultLetterId;
                      return (
                        <tr
                          key={letter.id}
                          className={`bg-stone-900/50 hover:bg-stone-800/60 transition-colors ${
                            isDefault ? 'ring-1 ring-inset ring-emerald-500/40' : ''
                          }`}
                          data-testid={`choose-preview-row-${letter.id}`}
                        >
                          <td className="px-4 py-3 text-white font-medium">{letter.bureau}</td>
                          <td className="px-4 py-3 text-slate-300">{letter.letterType || letter.method || '—'}</td>
                          <td className="px-4 py-3 text-slate-300 max-w-[180px] truncate">{letter.recipient}</td>
                          <td className={`px-4 py-3 font-medium ${statusClass(letter.lifecycleStatus)}`}>
                            {statusLabel ? statusLabel(letter.lifecycleStatus) : letter.lifecycleStatus}
                          </td>
                          <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                            {formatLetterDate(letter.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-center text-slate-300">{attachmentCount}</td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              size="sm"
                              onClick={() => onSelect?.(letter)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`choose-preview-select-${letter.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1.5" />
                              Preview
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChooseLetterPreviewModal;
